import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Phone, MapPin, Clock, Navigation, MessageCircle, CheckCircle2, Award, Microscope, HeartPulse, ShieldCheck, ArrowRight } from 'lucide-react';
import { CLINIC, IMAGES, CITY_PAGES, DISORDERS, RECOGNITION } from '../mock';
import { PageHero, SectionTitle, Reveal } from '../components/Shared';

const WHY = [
  { icon: Award, title: 'UK Consultant Expertise', body: 'UK-trained consultant rheumatologist with NHS experience and MRCP (Rheumatology).' },
  { icon: Microscope, title: 'Accurate Diagnosis', body: 'Advanced serology, imaging and musculoskeletal ultrasound for early, precise diagnosis.' },
  { icon: HeartPulse, title: 'Modern Treatment', body: 'Evidence-based care including DMARDs and the latest biologic therapies.' },
  { icon: ShieldCheck, title: 'Compassionate Care', body: 'Clear explanations and a supportive, patient-first approach.' },
];

export default function CityPage() {
  const { city } = useParams();
  const data = CITY_PAGES[city];
  if (!data) return <Navigate to="/our-locations" replace />;

  const map = `https://maps.google.com/maps?q=${data.mapQuery}&t=&z=13&ie=UTF8&iwloc=&output=embed`;

  return (
    <div>
      <PageHero
        eyebrow={`Rheumatologist in ${data.city}`}
        title={`Expert Rheumatologist in ${data.city}`}
        subtitle={`Specialist care for arthritis, lupus, spondylitis and autoimmune diseases \u2014 by Dr Puneet Srivastava at ${data.clinic}, ${data.city}.`}
        image={IMAGES[data.image]}
      />

      {/* Intro + clinic card */}
      <section className="py-16">
        <div className="container-x grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <SectionTitle eyebrow={`${data.city} Clinic`} title={`Rheumatology care, close to ${data.city}`} />
            {city === 'ghaziabad' && (
              <a data-testid="city-threebestrated-badge" href={RECOGNITION.url} target="_blank" rel="noreferrer"
                className="card-lift flex items-center gap-4 rounded-2xl p-4 mb-6 text-white" style={{ background: 'linear-gradient(140deg,#0b3b3a,#0f766e)' }}>
                <span className="grid place-items-center w-12 h-12 rounded-xl bg-white/10 shrink-0"><Award size={24} className="text-amber-300" /></span>
                <span>
                  <span className="block text-xs uppercase tracking-wider text-teal-100">Recognised by {RECOGNITION.source}</span>
                  <span className="block font-semibold">Top 3 Rheumatologist in Ghaziabad</span>
                </span>
              </a>
            )}
            <p className="text-slate-600 leading-relaxed">{data.intro}</p>
            <div className="grid sm:grid-cols-2 gap-3 mt-7">
              {['Arthritis & joint pain', 'Autoimmune diseases', 'Biologic therapy', 'Second opinions'].map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 size={17} className="text-teal-600" /> {t}</div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href={`tel:${CLINIC.phoneRaw}`} className="btn-coral inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold"><Phone size={18} /> Call {CLINIC.phone}</a>
              <Link to="/second-opinion" className="btn-outline inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold bg-white">Book Appointment <ArrowRight size={18} /></Link>
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-teal-50 shadow-sm overflow-hidden">
            <iframe title={data.clinic} src={map} className="w-full h-64 border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            <div className="p-7">
              <h3 className="font-display text-xl text-teal-900 mb-4" style={{ fontWeight: 600 }}>{data.clinic}</h3>
              <ul className="space-y-3">
                <li className="flex gap-3"><MapPin size={19} className="text-teal-600 shrink-0" /><span className="text-slate-600 text-sm">{data.address}</span></li>
                <li className="flex gap-3"><Clock size={19} className="text-teal-600 shrink-0" /><span className="text-slate-600 text-sm">{data.days}</span></li>
                <li className="flex gap-3"><Phone size={19} className="text-teal-600 shrink-0" /><a href={`tel:${CLINIC.phoneRaw}`} className="text-slate-600 text-sm hover:text-teal-700">{CLINIC.phone}</a></li>
              </ul>
              <div className="flex gap-3 mt-6">
                <a href={`https://maps.google.com/maps?q=${data.mapQuery}`} target="_blank" rel="noreferrer" className="btn-teal inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm"><Navigation size={15} /> Directions</a>
                <a href={CLINIC.whatsapp} target="_blank" rel="noreferrer" className="btn-outline inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm bg-white"><MessageCircle size={15} /> WhatsApp</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why choose */}
      <section className="mint-section py-16">
        <div className="container-x">
          <SectionTitle center eyebrow="Why Patients Choose Us" title={`Trusted rheumatology care for ${data.region}`} />
          <div className="grid md:grid-cols-4 sm:grid-cols-2 gap-6">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 80}>
                <div className="card-lift bg-white border border-teal-50 rounded-2xl p-7 h-full">
                  <span className="grid place-items-center w-14 h-14 rounded-2xl text-white mb-5" style={{ background: 'linear-gradient(135deg,#0f766e,#0b3b3a)' }}><w.icon size={26} /></span>
                  <h3 className="text-base text-teal-900 mb-2" style={{ fontWeight: 600 }}>{w.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Conditions treated */}
      <section className="py-16">
        <div className="container-x">
          <SectionTitle center eyebrow="Conditions" title={`Conditions we treat in ${data.city}`} />
          <div className="grid md:grid-cols-3 sm:grid-cols-2 gap-4">
            {DISORDERS.map((d) => (
              <div key={d.id} className="flex items-center gap-3 bg-white border border-teal-50 rounded-xl p-4">
                <span className="w-2 h-2 rounded-full bg-teal-500 shrink-0" />
                <span className="text-slate-700 text-sm font-medium">{d.name}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/disorders-we-treat" className="btn-outline inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-white">View all conditions <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* Other cities */}
      <section className="mint-section py-14">
        <div className="container-x">
          <h3 className="font-display text-xl text-teal-900 mb-5 text-center" style={{ fontWeight: 600 }}>Rheumatology care in other cities</h3>
          <div className="flex flex-wrap justify-center gap-3">
            {Object.values(CITY_PAGES).filter((c) => c.city !== data.city).map((c) => (
              <Link key={c.city} to={`/rheumatologist-in/${c.city.toLowerCase()}`} className="bg-white border border-teal-100 text-teal-800 text-sm font-medium px-5 py-2.5 rounded-full hover:bg-teal-600 hover:text-white transition-colors">
                Rheumatologist in {c.city}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
